import { useCallback, useEffect, useMemo, useState } from 'react'
import { MultiStepApprovalProps } from './MultiStepApproval.types'
import { useMarketplaceUser } from 'context/MarketplaceUser'
import { OfferConfirmationType, useDisplayModel } from 'context/DisplayModels'
import {
  AutoConfirmation,
  AutoConfirmationStatus,
} from 'pages/Authenticated/Marketplace/CreateOffer/Confirm/Confirm.view'
import Spacing from '@onepercentio/one-ui/components/Spacing'
import { useProgressUI } from 'openspace/service/Progress'

export default function MultiStepApprovalLogic({
  onTargetAction,
  onConfirm,
  label,
  ManualComponent,
  ...props
}: MultiStepApprovalProps) {
  const confirmationModel = useDisplayModel('offerConfirmation.mode')
  const [isAllowedForTransfer, setISAllowed] = useState<boolean>()
  const [targetActionResult, setTargetActionResult] = useState<boolean>()
  const {
    getNftMovementAllowance,
    getTokensAllowance,
    control,
    approveNFTManagement,
    approveTokenAllowance,
    createOfferOnly: _createOfferOnly,
  } = useMarketplaceUser()

  const checkIfIsAllowed = useCallback(() => {
    if (props.mode === 'erc1155') return getNftMovementAllowance(props.tokenId)
    else
      return getTokensAllowance(props.currency, props.price).then(
        (allowance) => {
          return allowance > 0
        }
      )
  }, [])

  useEffect(() => {
    checkIfIsAllowed().then(setISAllowed)
  }, [getNftMovementAllowance])

  const approveTransaction = useCallback(() => {
    if (props.mode === 'erc1155') return approveNFTManagement(props.tokenId)
    else return approveTokenAllowance(props.currency, props.price)
  }, [])

  function _approveNFTManagement() {
    return approveTransaction().then(() => setISAllowed(true))
  }

  function _retry() {
    if (isAllowedForTransfer === undefined)
      checkIfIsAllowed()
        .then(setISAllowed)
        .catch(() => {})
    if (isAllowedForTransfer === false) approveTransaction()
    if (isAllowedForTransfer === true) onTargetAction()
  }

  const triggerOnTargetAction = useCallback(
    () => onTargetAction().then(() => setTargetActionResult(true)),
    []
  )
  const creationProgress = useProgressUI('step_approval')

  switch (confirmationModel) {
    case OfferConfirmationType.AUTO:
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const status = useMemo(() => {
        if (isAllowedForTransfer === undefined)
          return AutoConfirmationStatus.LOADING
        if (isAllowedForTransfer === false)
          return AutoConfirmationStatus.NEED_ALLOWANCE
        if (targetActionResult === true) return AutoConfirmationStatus.CONFIRMED
        return AutoConfirmationStatus.NEED_CONFIRMATION
      }, [isAllowedForTransfer, targetActionResult])
      return (
        <>
          <div>
            <AutoConfirmation
              label={{
                confirmed: onConfirm ? label.confirmed : undefined,
                loading: label.loading,
              }}
              onApproveAllowance={_approveNFTManagement}
              onConfirmOperation={triggerOnTargetAction}
              onGoToItem={onConfirm}
              status={status}
              onCancel={props.onCancel}
              progressLabel={
                status !== AutoConfirmationStatus.CONFIRMED ? (
                  <>
                    <Spacing size='16' />
                    {creationProgress}
                  </>
                ) : null
              }
            />
          </div>
        </>
      )
    default:
      return (
        <ManualComponent
          loading={control.loading}
          error={!!control.error}
          isAllowedForTransfer
          onApproveTransfer={_approveNFTManagement}
          onTargetAction={triggerOnTargetAction}
          onConfirm={onConfirm}
          onRetry={_retry}
        />
      )
  }
}
